import React from "react";
import { useParams, Navigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Check, Activity, Brain, ShieldCheck } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import FAQ from "../components/FAQ";
import { Reveal } from "../components/Reveal";
import { conditionsData } from "../data/conditionsData";

const ConditionPage = () => {
  const { conditionId } = useParams();
  const condition = conditionsData[conditionId];

  if (!condition) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-white font-sans">
      <Navbar />

      {/* Hero Section */}
      <div className="relative w-full h-[500px] overflow-hidden flex items-center">
        <div 
          className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: "url('https://images.pexels.com/photos/4031818/pexels-photo-4031818.jpeg?auto=compress&cs=tinysrgb&w=1920&h=900&fit=crop')",
          }}
        >
          <div className="absolute inset-0 bg-[#012d5c]/75"></div>
        </div>
        
        <div className="relative z-10 container mx-auto px-4 flex flex-col justify-center h-full">
          <Reveal>
            <div className="max-w-3xl text-white">
              <span className="bg-[#4caf50] text-white px-3 py-1 rounded-lg text-sm font-bold uppercase tracking-wider mb-4 inline-block">
                {condition.category}
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                {condition.title}
              </h1>
              <p className="text-xl lg:text-2xl text-gray-100 mb-8 font-light leading-relaxed">
                Find out whether medical cannabis treatment may be clinically appropriate for your condition through a specialist assessment.
              </p>
              <Button
                className="bg-[#4caf50] hover:bg-[#3d8b40] text-white text-lg px-8 py-6 rounded-lg shadow-lg"
                onClick={() => window.location.href = "https://app.integroclinics.com/v2/registration"}
              >
                Check your eligibility
              </Button>
            </div>
          </Reveal>
        </div>
      </div>

      {/* About Condition Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-12 items-start">
            <div className="lg:w-1/2">
               <Reveal>
                 <h2 className="text-3xl font-bold text-[#012d5c] mb-6">Understanding {condition.title}</h2>
                 <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                   {condition.overview}
                 </p>
                 
                 <div className="bg-[#f0f4f8] border-l-4 border-[#005eb8] p-6 rounded-r-lg mb-6">
                   <h3 className="text-base font-bold text-[#012d5c] mb-3">About Clinical Assessment</h3>
                   <p className="text-gray-600 leading-relaxed text-sm">
                     {condition.clinicalContext}
                   </p>
                 </div>

                 <div className="bg-[#f0f4f8] p-6 rounded-xl border border-gray-100 mt-8">
                    <h4 className="font-bold text-[#012d5c] mb-4 flex items-center">
                        <Activity className="w-5 h-5 text-[#4caf50] mr-2" />
                        Common Symptoms
                    </h4>
                    <ul className="space-y-3">
                        {condition.symptoms.map((symptom, i) => (
                        <li key={i} className="flex items-center text-gray-700">
                            <div className="w-2 h-2 bg-[#4caf50] rounded-full mr-3"></div>
                            {symptom}
                        </li>
                        ))}
                    </ul>
                 </div>
               </Reveal>
            </div>
            
            <div className="lg:w-1/2">
                <Reveal delay={200}>
                  <div className="bg-[#012d5c] text-white p-8 lg:p-12 rounded-2xl shadow-xl">
                      <h3 className="text-2xl font-bold mb-6">Who is eligible?</h3>
                      <p className="mb-6 text-gray-300 leading-relaxed">
                          To be eligible for medical cannabis treatment in the UK, you typically must have:
                      </p>
                      <ul className="space-y-4 mb-8">
                          <li className="flex items-start">
                              <Check className="w-5 h-5 text-[#4caf50] mr-3 mt-1 shrink-0" />
                              <span>A diagnosed condition (like {condition.title})</span>
                          </li>
                          <li className="flex items-start">
                              <Check className="w-5 h-5 text-[#4caf50] mr-3 mt-1 shrink-0" />
                              <span>Tried at least two conventional treatments or medications that didn't work or caused severe side effects</span>
                          </li>
                          <li className="flex items-start">
                              <Check className="w-5 h-5 text-[#4caf50] mr-3 mt-1 shrink-0" />
                              <span>No history of psychosis (assessed on case-by-case basis)</span>
                          </li>
                      </ul>
                      <Button
                        className="w-full bg-[#4caf50] hover:bg-[#3d8b40] text-white text-lg py-6 rounded-lg"
                        onClick={() => window.location.href = "https://app.integroclinics.com/v2/registration"}
                        >
                        Take Pre-Screening Now
                        </Button>
                  </div>
                </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Strip - Centered and Stretched */}
      <section className="py-24 bg-[#f4f7fa] border-y border-gray-100">
        <div className="container mx-auto px-4 text-center">
          <Reveal>
            <div className="max-w-4xl mx-auto">
                <h2 className="text-4xl lg:text-5xl font-bold text-[#012d5c] mb-6">Ready to start your journey?</h2>
                <p className="text-xl text-gray-600 mb-10 leading-relaxed">
                    Complete our short eligibility check to find out whether you may be suitable for a specialist clinical assessment at Integro.
                </p>
                <Button
                    className="bg-[#005eb8] hover:bg-[#004a94] text-white px-12 py-8 rounded-xl text-xl shadow-xl hover:shadow-2xl transition-all transform hover:-translate-y-1 w-full sm:w-auto"
                    onClick={() => window.location.href = "https://app.integroclinics.com/v2/registration"}
                >
                    Check Your Eligibility
                </Button>
            </div>
          </Reveal>
        </div>
      </section>

      <FAQ />
      <Footer />
    </div>
  );
};

export default ConditionPage;
